/**
 * IconButton — Apple-style icon-only action button.
 *
 * Circular hover target with centered icon. Supports optional
 * badge count (e.g., notification count, item count).
 *
 * Consumer controls icon sizing — pass a Lucide icon with className.
 *
 * Usage:
 *   <IconButton icon={<Plus className="w-4 h-4" />} label="Add item" onClick={handleAdd} />
 *   <IconButton icon={<Bell className="w-5 h-5" />} label="Notifications" badge={3} />
 */

import { ds } from '../tokens';

export interface IconButtonProps {
  icon: React.ReactNode;
  /** Accessibility label (required for screen readers) */
  label: string;
  onClick?: (e: React.MouseEvent) => void;
  badge?: number;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'danger';
  /** Custom color for icon and badge background (hex/rgb). Overrides variant color. */
  color?: string;
  disabled?: boolean;
  className?: string;
}

const sizeConfig = {
  sm: { button: 'w-7 h-7',  badge: 'w-3.5 h-3.5 text-[9px]' },
  md: { button: 'w-8 h-8',  badge: 'w-4 h-4 text-[10px]' },
  lg: { button: 'w-10 h-10', badge: 'w-5 h-5 text-[11px]' },
};

export function IconButton({
  icon,
  label,
  onClick,
  badge,
  size = 'md',
  variant = 'default',
  color,
  disabled = false,
  className = '',
}: IconButtonProps) {
  const s = sizeConfig[size];

  const colorClass = color
    ? ''
    : variant === 'danger'
      ? ds.color.label.destructive
      : ds.color.label.secondary;

  const hoverClass = color
    ? ''
    : variant === 'danger'
      ? ds.color.fill.hoverDanger
      : ds.color.fill.hover;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className={[
        'group relative inline-flex items-center justify-center',
        ds.radius.full,
        s.button,
        colorClass,
        hoverClass,
        ds.transition.fast,
        disabled ? 'opacity-50 cursor-not-allowed' : 'active:scale-90',
        className,
      ].filter(Boolean).join(' ')}
      style={color ? { color } : undefined}
    >
      <span className={color ? 'group-hover:brightness-75' : ''}>
        {icon}
      </span>

      {/* Badge count — darkens on hover without affecting text color */}
      {badge !== undefined && (
        <span
          className={[
            'absolute -top-0.5 -right-0.5 flex items-center justify-center overflow-hidden',
            ds.radius.full,
            color ? '' : ds.color.fill.destructive,
            'text-white',
            ds.type.weight.semibold,
            s.badge,
          ].filter(Boolean).join(' ')}
          style={color ? { backgroundColor: color } : undefined}
        >
          <span className={`absolute inset-0 ${ds.radius.full} bg-black/0 group-hover:bg-black/15 ${ds.transition.fast}`} />
          <span className="relative">{badge > 99 ? '99+' : badge}</span>
        </span>
      )}
    </button>
  );
}
